import { useEffect, useState } from 'react';
import { Box, Group, Stack, Text, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';
import { playBleep } from '@/utils/audio';

const AUTO_RESET_DELAY = 5000; // 5 seconds

export function SuccessScreen() {
  const { state, reset } = useKiosk();
  const [timeLeft, setTimeLeft] = useState(AUTO_RESET_DELAY / 1000);

  // Audio cue on mount (I22 — operators often look at the radio, not the
  // screen, so visual feedback alone is missed).
  useEffect(() => {
    playBleep('success');
  }, []);

  // Auto-reset after delay
  useEffect(() => {
    const timer = setTimeout(reset, AUTO_RESET_DELAY);
    return () => clearTimeout(timer);
  }, [reset]);

  // Countdown timer -- decrements at 100ms intervals for smooth ring drain
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 0.1));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const circumference = 2 * Math.PI * 88;
  const progress = (timeLeft / (AUTO_RESET_DELAY / 1000)) * circumference;

  return (
    <Stack
      align="center"
      gap={40}
      onClick={reset}
      py={24}
      style={{ cursor: 'pointer' }}
    >
      {/* ----------------------------------------------------------------
          Success Icon with Countdown Ring -- A celebratory, multi-
          layered composition. The outer ring drains smoothly to
          show time remaining. The inner icon uses the success-
          celebrate class for a bounce + ripple entrance, and the
          checkmark draws itself with a stroke animation.
          ---------------------------------------------------------------- */}
      <Box
        style={{
          position: 'relative',
          width: 'min(200px, 45vw)',
          height: 'min(200px, 45vw)',
        }}
      >
        {/* Countdown ring -- SVG circle that drains linearly */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 200 200"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'rotate(-90deg)',
          }}
        >
          {/* Track ring (background) */}
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke="rgba(34, 197, 94, 0.15)"
            strokeWidth="6"
          />
          {/* Progress ring (draining) */}
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke="var(--cmd-success)"
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.1s linear',
              filter: 'drop-shadow(0 0 6px var(--cmd-success-glow))',
            }}
          />
        </svg>

        {/* Centering wrapper — keeps translate separate from the
            bounce animation's transform so they don't conflict */}
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
          }}
        >
        <Box
          className="kiosk-success-celebrate"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--cmd-success) 0%, #16A34A 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px var(--cmd-success-glow), 0 2px 8px rgba(0, 0, 0, 0.10)',
          }}
        >
          {/* Animated checkmark -- draws itself via stroke-dashoffset */}
          <svg
            width="60"
            height="60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline
              points="20 6 9 17 4 12"
              style={{
                strokeDasharray: 48,
                strokeDashoffset: 48,
                animation: 'kioskCheckmarkDraw 0.5s var(--ease-out) 0.3s forwards',
              }}
            />
          </svg>
        </Box>
        </Box>
      </Box>

      {/* ----------------------------------------------------------------
          Success Message -- Large, confident heading with the
          detail message below. The green colour reinforces the
          positive outcome.
          ---------------------------------------------------------------- */}
      <Stack align="center" gap="md" mt={8}>
        <Title
          order={1}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--cmd-success)',
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
            letterSpacing: '-0.02em',
          }}
        >
          Success
        </Title>

        <Text
          ta="center"
          maw={480}
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '1.0625rem',
            lineHeight: 1.6,
          }}
        >
          {state.successMessage}
        </Text>
      </Stack>

      {/* ----------------------------------------------------------------
          Hint Bar -- Subtle glass pill telling the user they can
          tap to dismiss or wait for the auto-reset countdown.
          ---------------------------------------------------------------- */}
      <Box
        mt={16}
        style={{
          padding: 'var(--space-3) var(--space-6)',
          borderRadius: 'var(--radius-full)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
        }}
      >
        <Group gap={8}>
          <Box
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'var(--cmd-success)',
              boxShadow: '0 0 8px var(--cmd-success-glow)',
              animation: 'kioskPulse 1.5s var(--ease-in-out) infinite',
            }}
          />
          <Text
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
            }}
          >
            Tap anywhere to continue or wait {Math.ceil(timeLeft)}s
          </Text>
        </Group>
      </Box>
    </Stack>
  );
}
